import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  Spin,
  message,
  Upload,
  ConfigProvider
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  LoadingOutlined,
  CameraOutlined,
  LinkedinOutlined,
  TeamOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Link } = Typography;

const ConsultantProfile = () => {
  const navigate = useNavigate();
  const brandColor = '#3C007A';
  const [loading, setLoading] = useState(true);
  const [consultantData, setConsultantData] = useState(null);
  const [profilePictureError, setProfilePictureError] = useState(false);
  const [bannerError, setBannerError] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Default fallback images
  const defaultProfilePicture = 'https://ui-avatars.com/api/?name=Consultant&background=3C007A&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=2074&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchConsultantProfile();
  }, []);

  const fetchConsultantProfile = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      if (userInfo.consultant) {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/consultants/${userInfo.consultant}`,
          {
            headers: {
              'x-auth-token': token
            }
          }
        );
        setConsultantData(response.data);
      } else {
        message.error('No consultant ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load consultant profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('profilePicture', file);

      const token = localStorage.getItem('token');
      // Don't set Content-Type manually — axios sets it automatically for
      // FormData with the correct multipart boundary.
      await axios.put(
        `${API_URL}/api/consultants/${userInfo.consultant}`,
        formData,
        {
          headers: {
            'x-auth-token': token
          }
        }
      );
      message.success('Profile picture uploaded successfully');
      fetchConsultantProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload profile picture');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'verified': 'success',
      'pending': 'warning',
      'active': 'success',
      'approved': 'success',
      'inactive': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      'approved': 'Verified',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'active': 'Active',
      'verified': 'Verified',
      'inactive': 'Inactive'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!consultantData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No consultant data available. Please contact support.</Text>
      </div>
    );
  }

  const profilePictureUrl = !profilePictureError && consultantData.profilePicture 
    ? getImageUrl(consultantData.profilePicture) 
    : defaultProfilePicture;
  
  const bannerUrl = defaultBanner;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Banner Section */}
        <div style={{
          height: 'clamp(200px, 30vh, 320px)',
          background: `url(${bannerError ? defaultBanner : bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(60, 0, 122, 0.3), rgba(0,0,0,0.6))'
          }} />

          {/* Hidden image to check if banner loads */}
          <img 
            src={bannerUrl} 
            style={{ display: 'none' }}
            onError={() => setBannerError(true)}
            alt="banner"
          />

          {/* Edit Profile Button */}
          <Button
            icon={<EditOutlined />}
            type="primary"
            size="large"
            style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              zIndex: 10,
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '44px',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '8px',
              backgroundColor: brandColor,
              borderColor: brandColor,
              boxShadow: '0 4px 12px rgba(60, 0, 122, 0.3)',
            }}
          >
            Edit Profile
          </Button>
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 24px 24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  position: 'relative',
                  display: 'inline-block'
                }}>
                  <div style={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {profilePictureError || !consultantData?.profilePicture ? (
                      <Avatar
                        size={130}
                        style={{
                          backgroundColor: brandColor,
                          fontSize: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                        icon={<UserOutlined />}
                      >
                        {consultantData?.fullName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <img
                        src={profilePictureUrl}
                        alt="Consultant Profile"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={() => setProfilePictureError(true)}
                      />
                    )}
                  </div>

                  {/* Upload Profile Picture Button */}
                  <Upload
                    showUploadList={false}
                    beforeUpload={handleProfilePictureUpload}
                    accept="image/*"
                  >
                    <Button
                      icon={<CameraOutlined />}
                      shape="circle"
                      style={{
                        position: 'absolute',
                        bottom: 5,
                        right: 5,
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Upload>
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px'
                    }}
                  >
                    {consultantData.fullName || 'Consultant Name'}
                  </Title>
                  <Text style={{
                    fontSize: '18px',
                    color: '#666',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400
                  }}>
                    <TrophyOutlined /> {consultantData.expertise}
                  </Text>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      Consultant
                    </Tag>
                    {consultantData.verificationStatus === 'verified' && (
                      <Tag
                        icon={<SafetyCertificateOutlined />}
                        color="success"
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        Verified
                      </Tag>
                    )}
                    {consultantData.status && (
                      <Tag
                        color={getStatusColor(consultantData.status)}
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {getStatusText(consultantData.status)}
                      </Tag>
                    )}
                  </Space>
                  {consultantData.website && (
                    <Link
                      href={consultantData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333"
                      }}
                    >
                      <GlobalOutlined /> {consultantData.website}
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            {/* Left Column: Professional Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <TeamOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Professional Details
                  </Space>
                }
                bordered={false}
                style={{ height: 'auto' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    textAlign: 'center'
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}
                >
                  <Descriptions.Item label="Primary Expertise">
                    <Text strong style={{ fontSize: 18, fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                      {consultantData.expertise || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="Years of Experience">
                    <Space>
                      <Text strong style={{ fontSize: 17, fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {consultantData.yearsOfExperience} years
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  {consultantData.consultingFields && consultantData.consultingFields.length > 0 && (
                    <Descriptions.Item label="Consulting Fields" span={2}>
                      <div style={{ marginLeft: '0', width: '100%' }}>
                        {consultantData.consultingFields.map((field, index) => (
                          <div 
                            key={index}
                            style={{ 
                              display: 'flex',
                              alignItems: 'flex-start',
                              fontFamily: "'Rubik', sans-serif",
                              fontSize: '17px',
                              fontWeight: 400,
                              color: brandColor,
                              marginBottom: '4px',
                              width: '100%',
                              maxWidth: '100%'
                            }}
                          >
                            <span style={{
                              color: brandColor,
                              marginRight: '10px',
                              flexShrink: 0,
                              lineHeight: '1.5'
                            }}>•</span>
                            <span style={{ 
                              flex: 1,
                              wordBreak: 'normal',
                              overflowWrap: 'break-word',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: '1.5',
                              maxWidth: 'calc(100% - 20px)'
                            }}>
                              {field}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {consultantData.description && (
                <Card
                  title={
                    <Space style={{
                      fontSize: '20px',
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      color: brandColor
                    }}>
                      About
                    </Space>
                  }
                  bordered={false}
                  style={{ marginTop: 24 }}
                >
                  <Text
                    style={{
                      display: 'block',
                      lineHeight: 1.8,
                      fontSize: '16px',
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      color: '#333'
                    }}
                  >
                    {consultantData.description}
                  </Text>
                </Card>
              )}
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    <EnvironmentOutlined style={{ color: brandColor }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none',
                  padding: '12px 12px 0 12px'
                }}
                bodyStyle={{
                  padding: '24px'
                }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  
                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <MailOutlined style={{ fontSize: 22, color: brandColor }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                        Email
                      </Text>
                      <Link href={`mailto:${consultantData.email}`} style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                        {consultantData.email}
                      </Link>
                    </div>
                  </div>

                  {consultantData.phone && (
                    <>
                      <Divider style={{ margin: '3px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <PhoneOutlined style={{ fontSize: 22, color: brandColor }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            Phone
                          </Text>
                          <Link href={`tel:${consultantData.phone}`} style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                            {consultantData.phone}
                          </Link>
                        </div>
                      </div>
                    </>
                  )}

                  {consultantData.linkedinProfile && (
                    <>
                      <Divider style={{ margin: '3px 0' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          backgroundColor: '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <LinkedinOutlined style={{ fontSize: 22, color: brandColor }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                            LinkedIn
                          </Text>
                          <Link href={consultantData.linkedinProfile} target="_blank" style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block', color: brandColor }}>
                            View Profile
                          </Link>
                        </div>
                      </div>
                    </>
                  )}

                  <Divider style={{ margin: '3px 0' }} />
                  
                  {/* Location */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <EnvironmentOutlined style={{ fontSize: 22, color: brandColor }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 14, fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                        Location
                      </Text>
                      <Text style={{ fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 400, textAlign: 'left', display: 'block' }}>
                        {consultantData.city}, {consultantData.state}
                      </Text>
                      <Text style={{ display: 'block', fontSize: 15, color: '#999', fontFamily: "'Rubik', sans-serif", textAlign: 'left' }}>
                        {consultantData.country}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ConsultantProfile;
